import { action, computed, observable } from 'mobx';

import { ViewController } from '@estee/elc-service';
import { DateFormatter, NumberFormatter } from '@estee/elc-formatters';

import { LoyaltyRepository } from '~repositories/LoyaltyRepository';
import { LoadLoyaltyInfoUseCase } from '~use-cases/LoadLoyaltyInfoUseCase';
import { IRewardsLink } from '~views/PointsInfo';
import { emit, events } from '@estee/elc-service-bus';

import { IRewardsLinkInfo } from './LoyaltyPointsInfo';
import { ITierLevelInfo } from '~exported/loyalty-tier-info/LoyaltyTierInfo';
import {
    ILoyaltyEnrollmentResponse,
    IUserLoyaltyEnrollment
} from '~api/interfaces/ILoyaltyEnrollmentResponse';
import { IBirthDate } from '~exported/loyalty-birthday/ILoyaltyBirthday';

import { ILink } from '~views/components/BaseLink';
import { IModalInfo } from '~views/LoyaltyStatusInfo';

export interface IConfigParamsDateNumberFormats {
    defaultAnniversaryDateFormat?: string;
    groupingFormatForComma?: boolean;
    loyaltyConfig: {
        version: string;
        modalInfo: IModalInfo;
        loyaltyPointsInfo: IRewardsLinkInfo;
        loyaltyPointsDetail: IRewardsLinkInfo;
        loyaltyTierInfo: ITierLevelInfo;
        displayTierName: boolean;
        tierPrefix: string;
        avlPointsText: string;
        pointsExpireText: string;
        highestTierText: string;
    };
}

export interface ILoyaltyPointsInfoController {
    loyaltyRepository: LoyaltyRepository;
    dateFormatter: DateFormatter;
    numberFormatter: NumberFormatter;
    locale: string;
}

class LoyaltyPointsInfoController extends ViewController {
    private loyaltyRepository: LoyaltyRepository;
    private dateFormatter: DateFormatter;
    private numberFormatter: NumberFormatter;
    private locale: string;
    @observable public birthDay: IBirthDate;
    @observable public loyaltyCheck: boolean;
    @observable private configParams: IConfigParamsDateNumberFormats;
    @observable private enrolmentErrorMsg: string;
    @observable public isJoinNowDisable: boolean = false;

    constructor({
        dateFormatter,
        loyaltyRepository,
        numberFormatter
    }: ILoyaltyPointsInfoController) {
        super();
        this.dateFormatter = dateFormatter;
        this.loyaltyRepository = loyaltyRepository;
        this.numberFormatter = numberFormatter;
    }

    public onPointsInfoLinkClick = (loyaltyLinkData: IRewardsLink) => {
        const { label } = loyaltyLinkData;
        emit(events.ACCOUNT_LOYALTY_POINTS_INFO_LINK_CLICKED, { label });
    };

    @action
    public setConfigParams = (configParams: IConfigParamsDateNumberFormats) => {
        this.configParams = configParams;
    };

    onBirthDaySelected = (birthDay: IBirthDate): void => {
        this.birthDay = birthDay;
    };

    onTermsAndConditionCheck = (isChecked: boolean, value: string) => {
        this.loyaltyCheck = isChecked;
    };

    @action
    public setErrorInEnrolment = (enrolmentErrorMsg: string) => {
        this.enrolmentErrorMsg = enrolmentErrorMsg;
    };

    @action
    public setJoinNowDisable = (isJoinNowDisable: boolean) => {
        this.isJoinNowDisable = isJoinNowDisable;
    };

    onLearnMoreClick = (learnMoreLink: ILink) => {
        emit(events.ACCOUNT_NONLOYALTY_ENROLLMENT_LINK_CLICKED, learnMoreLink);
    };

    @action
    onJoinNowClick = async (birthDayValues: IBirthDate) => {
        let userBirthDayInfo;
        const { birthDay = '', birthMonth = '' } = this.loyaltyRepository?.loyaltyInfo || {};
        if (birthDay && birthMonth) {
            userBirthDayInfo = {
                day: birthDay,
                month: birthMonth
            };
        } else {
            userBirthDayInfo = {
                day: birthDayValues?.day || '',
                month: birthDayValues?.month || ''
            };
        }

        this.setJoinNowDisable(true);
        const loyaltyLastEnrolledSource =
            this.loyaltyRepository?.loyaltyInfo?.loyaltyEnrolledSource || '';
        const { day, month } = userBirthDayInfo;
        const loyaltyEnrollmentData: IUserLoyaltyEnrollment = {
            birthdayDay: day,
            birthdayMonth: month,
            loyaltyEnrolledSource: loyaltyLastEnrolledSource,
            acceptedLoyaltyTerms: true
        };
        const enrolmentResult: ILoyaltyEnrollmentResponse =
            await this.loyaltyRepository.postEnrollmentData(loyaltyEnrollmentData);

        if (enrolmentResult.errorMessage) {
            this.setErrorInEnrolment(enrolmentResult.errorMessage as string);
        } else {
            const loyaltyFlag = enrolmentResult?.enrollUser?.loyaltyActiveFlag;
            emit(events.ACCOUNT_NONLOYALTY_ENROLLMENT_SIGNUP_CLICKED, { loyaltyFlag });
            emit(events.LOYALTY_ACCOUNT_ENROLMENT, { loyaltyFlag });
        }
    };

    @computed public get anniversaryDate() {
        const nextAnnDateValue = this.loyaltyRepository?.loyaltyInfo?.anniversaryDate;

        return nextAnnDateValue
            ? this.dateFormatter.formatDateWithPlaceholder({
                  message: '::LOYALTY_ANNIVERSARY_DATE::',
                  date: nextAnnDateValue,
                  dateFormat: this.configParams.defaultAnniversaryDateFormat,
                  placeholderRegex: /::LOYALTY_ANNIVERSARY_DATE::/gi
              })
            : null;
    }

    @computed public get formattedPointsValue() {
        const pointsValue = this.loyaltyRepository?.loyaltyInfo?.currentPoints;

        return this.numberFormatter.formatNumber(pointsValue, {
            shouldUseGrouping: this.configParams.groupingFormatForComma
        });
    }

    @action
    public loaded = async () => {
        this.isLoading = true;
        await new LoadLoyaltyInfoUseCase().execute();
        this.isLoading = false;
    };

    @computed
    public get data() {
        return {
            loyaltyInfo: this.loyaltyRepository?.loyaltyInfo,
            enrolmentErrorMsg: this.enrolmentErrorMsg,
            isDisabled: this.isJoinNowDisable,
            locale: this.locale
        };
    }

    @computed
    public get configs() {
        return this.configParams?.loyaltyConfig;
    }
}

export default LoyaltyPointsInfoController;
